/**
 * ranges.ts — resolves the ideal range for each CoachParameter from the
 * correct authoritative source (no new numbers invented here):
 *
 *   alk / ca / mg / pH  →  PROBE_BANDS[id].green  (monitor bands)
 *   salinity / nitrate / phosphate  →  catalog idealRange via getIdealRangeForParameter
 *
 * Throws a typed RangeResolutionError if a catalog-backed parameter has no
 * idealRange defined (which would mean the catalog is incomplete).
 */
import { getIdealRangeForParameter } from '@reefapp/shared';
import type { ParameterKey } from '@reefapp/shared';

import { PROBE_BANDS } from '../../lib/probe-thresholds';
import type { ProbeId } from '../../lib/probe-thresholds';

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type CoachParameter =
  | 'alk'
  | 'ca'
  | 'mg'
  | 'pH'
  | 'salinity'
  | 'nitrate'
  | 'phosphate';

export class RangeResolutionError extends Error {
  constructor(param: CoachParameter, detail: string) {
    super(`[ranges] No ideal range for coach parameter "${param}": ${detail}`);
    this.name = 'RangeResolutionError';
  }
}

// ---------------------------------------------------------------------------
// Internal mapping tables (not exported — internal wiring only)
// ---------------------------------------------------------------------------

/** CoachParameters whose range comes from PROBE_BANDS.green */
const PROBE_BAND_MAP: Record<'alk' | 'ca' | 'mg' | 'pH', ProbeId> = {
  alk: 'alk',
  ca: 'ca',
  mg: 'mg',
  pH: 'pH',
};

/** CoachParameters whose range comes from the test-kit catalog idealRange */
const CATALOG_MAP: Record<'salinity' | 'nitrate' | 'phosphate', ParameterKey> = {
  salinity: 'salinity',
  nitrate: 'nitrate',
  phosphate: 'phosphate',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Return { min, max } for the given CoachParameter.
 * Throws `RangeResolutionError` if a catalog-backed parameter has no range.
 */
export function getRangeForCoachParameter(
  param: CoachParameter,
): { min: number; max: number } {
  if (param in PROBE_BAND_MAP) {
    const probeId = PROBE_BAND_MAP[param as keyof typeof PROBE_BAND_MAP];
    const [min, max] = PROBE_BANDS[probeId].green;
    return { min, max };
  }

  // Catalog-backed parameters
  const paramKey =
    CATALOG_MAP[param as keyof typeof CATALOG_MAP];
  const range = getIdealRangeForParameter(paramKey);
  if (!range) {
    throw new RangeResolutionError(
      param,
      `no kit in catalog defines an idealRange for parameterKey="${paramKey}"`,
    );
  }
  return range;
}
